import { Check, Lock, RotateCcw } from "lucide-react";
import { BillStatus } from "@boo/shared";
import type { BillShares } from "@boo/shared";
import { Button } from "@/components/ui/button";
import { formatBaht } from "../lib/billMath";
import type { BillEditor } from "../types/bills.types";
import PersonAvatar from "./PersonAvatar";

interface SharesFooterProps {
  editor: BillEditor;
  shares: BillShares;
  total: number;
  saving: boolean;
  hidden?: boolean;
  onSave: () => void;
  onFinalize: () => void;
  onReopen: () => void;
}

export default function SharesFooter({
  editor,
  shares,
  total,
  saving,
  hidden = false,
  onSave,
  onFinalize,
  onReopen,
}: SharesFooterProps) {
  const finalized = editor.status === BillStatus.Finalized;
  const unclaimed = shares.unclaimedItemIds.length;
  const zeroAmounts = editor.items.filter((i) => i.amount === 0).length;
  const canFinalize =
    editor.items.length > 0 && unclaimed === 0 && zeroAmounts === 0;
  const diff =
    editor.transactionAmount != null
      ? Math.round((editor.transactionAmount - total) * 100) / 100
      : 0;

  return (
    <div
      className={`fixed inset-x-0 bottom-0 z-30 border-t border-border bg-card/95 backdrop-blur pb-[env(safe-area-inset-bottom)] ${hidden ? "hidden sm:block" : ""}`}
    >
      <div className="mx-auto max-w-[1240px] px-4 md:px-8 py-3 space-y-2.5">
        <div className="-mx-4 flex snap-x scroll-px-4 gap-2 overflow-x-auto overscroll-x-contain px-4 py-1 scrollbar-none after:w-2 after:shrink-0 after:content-['']">
          {editor.people.map((p) => {
            const share = shares.shares.find((s) => s.personId === p.id);
            const amount = finalized
              ? (editor.finalAmounts[p.id] ?? share?.rounded ?? 0)
              : (share?.rounded ?? 0);
            const paid = editor.paid[p.id];
            return (
              <div
                key={p.id}
                className="shrink-0 snap-start rounded-lg border border-border px-2.5 py-1.5"
              >
                <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                  <PersonAvatar person={p} size={16} />
                  {p.name}
                  {finalized && !p.isOwner && paid && (
                    <Check className="w-3 h-3 text-primary" />
                  )}
                </div>
                <div className="text-sm font-semibold text-foreground [font-variant-numeric:tabular-nums]">
                  {formatBaht(amount)}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
          <div className="min-w-0 text-xs text-muted-foreground [font-variant-numeric:tabular-nums]">
            <div className="flex flex-wrap items-center">
              <span>Bill {formatBaht(total)}</span>
              {!finalized && unclaimed > 0 && (
                <span className="ml-2 text-destructive">
                  {unclaimed} unassigned
                </span>
              )}
              {!finalized && zeroAmounts > 0 && (
                <span className="ml-2 text-destructive">
                  {zeroAmounts} with 0 amount
                </span>
              )}
              {finalized && (
                <span className="ml-2 inline-flex items-center gap-1">
                  <Lock className="w-3 h-3" /> Locked
                </span>
              )}
            </div>
            {Math.abs(diff) >= 0.5 && (
              <div className="line-clamp-1 sm:line-clamp-none">
                You paid {formatBaht(editor.transactionAmount!)}, the{" "}
                {formatBaht(Math.abs(diff))} difference is in your share.
              </div>
            )}
          </div>
          <div className="flex gap-2 [&>button]:flex-1 sm:[&>button]:flex-none">
            {finalized ? (
              <Button
                variant="outline"
                size="sm"
                disabled={saving}
                onClick={onReopen}
              >
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                Reopen
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={
                    saving || !editor.dirty || editor.items.length === 0
                  }
                  onClick={onSave}
                >
                  Save
                </Button>
                <Button
                  size="sm"
                  disabled={saving || !canFinalize}
                  onClick={onFinalize}
                >
                  Finalise
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
